import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Tag, BookOpen } from 'lucide-react';
import SEO from '../../../components/SEO';
import LandingNavBar from '../../../components/LandingNavBar';
import LandingFooter from '../../../components/LandingFooter';
import blogArticles, { formatBlogDate } from './blogArticles';

const seoGuides = [
  { to: '/malaysia-einvoice-software', title: 'Malaysia E-Invoice Software', desc: 'Automate LHDN compliance with StayMii e-invoice software for hotels, homestays, and businesses.', tag: 'E-Invoice' },
  { to: '/hotel-pms-malaysia', title: 'Hotel PMS Malaysia', desc: 'Cloud-based hotel property management system built for the Malaysian hospitality market.', tag: 'Hotel PMS' },
  { to: '/hotel-einvoice-system-malaysia', title: 'Hotel E-Invoice System Malaysia', desc: 'Automated LHDN e-invoice generation from PMS bookings, OTA reservations, and guest billing.', tag: 'Hotel E-Invoice' },
  { to: '/homestay-management-software-malaysia', title: 'Homestay Management Software Malaysia', desc: 'Manage bookings, guest check-ins, and LHDN e-invoicing for homestays and Airbnb properties.', tag: 'Homestay' },
  { to: '/property-management-software-malaysia', title: 'Property Management Software Malaysia', desc: 'Multi-property management with centralised booking, billing, and LHDN compliance.', tag: 'Property Mgmt' },
  { to: '/hotel-pms-penang', title: 'Hotel PMS Penang', desc: 'PMS built in Penang for Penang hotels. Local support, Malaysian pricing, LHDN e-invoice ready.', tag: 'Penang' },
  { to: '/what-is-malaysia-einvoice', title: 'What is Malaysia E-Invoice? Guide 2026', desc: 'Complete guide to Malaysia e-invoice — who must comply, deadlines, penalties, and how to get started.', tag: 'Guide' },
  { to: '/lhdn-einvoice-guide-hotels', title: 'LHDN E-Invoice Guide for Hotels', desc: 'Step-by-step LHDN compliance guide covering room bookings, OTA invoicing, tourism tax, and SST.', tag: 'LHDN Guide' },
  { to: '/staymii-hr', title: 'Free HR & Applicant Tracking Malaysia', desc: 'Generate a free public job application form in 60 seconds. Telegram alerts for every new applicant. Built for Malaysian SMEs.', tag: 'Free HR' },
];

const Reveal = ({ children, delay = 0 }) => (
  <div className="animate-fade-in" style={{ animationDelay: `${delay}ms`, animationFillMode: 'backwards' }}>
    {children}
  </div>
);

const categoryColors = {
  'Compliance': 'bg-red-500/10 text-red-400 border-red-500/20',
  'Setup Guide': 'bg-green-500/10 text-green-400 border-green-500/20',
  'Integration': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'Technical': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  'HR Guide': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
};

const guideTagColors = {
  'E-Invoice': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'Hotel PMS': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  'Hotel E-Invoice': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  'Homestay': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  'Property Mgmt': 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  'Penang': 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  'Guide': 'bg-teal-500/10 text-teal-400 border-teal-500/20',
  'LHDN Guide': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  'Free HR': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
};

const BlogIndex = () => {
  return (
    <div className="min-h-screen bg-[#0B1121]" data-testid="blog-index-page">
      <LandingNavBar />
      <SEO
        title="Blog — Hotel e-Invoice Malaysia Guides, Tips & Compliance Articles"
        description="Expert guides on LHDN e-Invoice for Malaysian hotels, Airbnb hosts, and short-term rentals. Setup tutorials, PMS integration guides, and tax compliance articles."
        keywords="hotel e invoice blog, lhdn e invoice guide, malaysia hotel tax blog, pms e invoice articles, airbnb e invoice malaysia"
        canonicalUrl="/blog"
      />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4" data-testid="blog-hero">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-300 text-sm font-medium mb-6">
              <Tag className="h-4 w-4" /> Knowledge Base
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Hotel e-Invoice Malaysia
              <span className="block text-[#60A5FA] mt-2">Guides & Articles</span>
            </h1>
            <p className="text-white/60 text-base lg:text-lg max-w-2xl mx-auto">
              Expert guides on LHDN e-Invoice compliance, PMS integration, and tax automation for Malaysian hotels, Airbnb hosts, and short-term rental operators.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Featured Article */}
      <section className="pb-8 px-4" data-testid="blog-featured">
        <div className="max-w-5xl mx-auto">
          <Reveal delay={100}>
            <Link
              to={`/blog/${blogArticles[0].slug}`}
              className="block bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700/50 rounded-2xl p-8 lg:p-10 hover:border-blue-500/30 transition-all group"
              data-testid="featured-article-card"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${categoryColors[blogArticles[0].category]}`}>
                  {blogArticles[0].category}
                </span>
                <span className="text-white/40 text-sm flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" /> {blogArticles[0].readTime}
                </span>
                <span className="text-white/30 text-sm">{formatBlogDate(blogArticles[0].date)}</span>
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3 group-hover:text-[#60A5FA] transition-colors">
                {blogArticles[0].title}
              </h2>
              <p className="text-white/50 text-base lg:text-lg mb-4 max-w-3xl">
                {blogArticles[0].description}
              </p>
              <span className="inline-flex items-center gap-2 text-[#60A5FA] font-medium text-sm group-hover:gap-3 transition-all">
                Read Article <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Guides & Resources */}
      <section className="py-12 px-4" data-testid="blog-guides">
        <div className="max-w-5xl mx-auto">
          <Reveal delay={120}>
            <div className="flex items-center gap-3 mb-8">
              <BookOpen className="h-5 w-5 text-[#60A5FA]" />
              <h2 className="text-2xl font-bold text-white">Guides & Resources</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {seoGuides.map((guide, i) => (
                <Link
                  key={guide.to}
                  to={guide.to}
                  className="block bg-slate-800/50 border border-slate-700/40 rounded-xl p-5 hover:border-blue-500/30 hover:bg-slate-800/70 transition-all group h-full"
                  data-testid={`guide-card-${guide.to.replace(/\//g, '')}`}
                >
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-medium border mb-3 ${guideTagColors[guide.tag] || 'bg-slate-500/10 text-slate-400 border-slate-500/20'}`}>
                    {guide.tag}
                  </span>
                  <h3 className="text-sm font-semibold text-white mb-2 group-hover:text-[#60A5FA] transition-colors leading-snug">
                    {guide.title}
                  </h3>
                  <p className="text-white/40 text-xs leading-relaxed mb-3 line-clamp-3">
                    {guide.desc}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-[#60A5FA] text-xs font-medium group-hover:gap-2.5 transition-all">
                    Read More <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Article Grid */}
      <section className="py-12 px-4" data-testid="blog-grid">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogArticles.slice(1).map((article, i) => (
              <Reveal key={article.slug} delay={150 + i * 80}>
                <Link
                  to={`/blog/${article.slug}`}
                  className="block bg-slate-800/50 border border-slate-700/40 rounded-xl p-6 hover:border-blue-500/30 hover:bg-slate-800/70 transition-all group h-full"
                  data-testid={`article-card-${article.slug}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${categoryColors[article.category]}`}>
                      {article.category}
                    </span>
                    <span className="text-white/30 text-xs flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {article.readTime}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#60A5FA] transition-colors leading-snug">
                    {article.title}
                  </h3>
                  <p className="text-white/40 text-sm leading-relaxed mb-4 line-clamp-3">
                    {article.description}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-[#60A5FA] text-sm font-medium group-hover:gap-2.5 transition-all">
                    Read More <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4" data-testid="blog-cta">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
              Ready to Automate Your Hotel e-Invoicing?
            </h2>
            <p className="text-white/50 mb-8 text-base">
              Join Malaysian hotels using StayMii to stay LHDN-compliant. Setup takes less than 30 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/get-started" className="px-8 py-3.5 bg-[#2563EB] hover:bg-[#1d4ed8] text-white rounded-xl font-medium transition-colors" data-testid="blog-cta-get-started">
                Get Started Free
              </Link>
              <Link to="/pricing" className="px-8 py-3.5 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-xl font-medium transition-colors" data-testid="blog-cta-pricing">
                View Pricing
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default BlogIndex;
